import type { RentalType } from 'src/features/business/leads/schemas/rental-type'
import { rentalTypeMap } from 'src/features/business/leads/schemas/rental-type'
import type { ResidenceType } from 'src/features/business/leads/schemas/residence-type'
import { residenceTypeMap } from 'src/features/business/leads/schemas/residence-type'
import type { BusinessStatus } from 'src/features/business/shared/schemas/business'
import { CalendarAppointmentIcon } from 'src/features/lynk/icons/calendar-appointment-icon'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import type {
  ClaimItem,
  ProjectType,
  ContractStatus,
  DateFlexibility,
  RefinanceReason,
  EmploymentStatus,
  CreditScore,
  LoanPurpose,
  Timeframe,
  PropertyType,
  ClientType,
} from 'src/shared/schemas/referrals'
import {
  clientTypeMap,
  propertyTypeMap,
  loanPurposeMap,
  titleTransactionTypeMap,
  dateFlexibilityMap,
  projectTypeMap,
  contractStatusMap,
  refinanceReasonMap,
  employmentStatusMap,
  creditScoreMap,
  timeframeMap,
} from 'src/shared/schemas/referrals'
import type { Budget, PriceRange } from 'src/shared/schemas/referrals/referral-fields/shared/price'
import type { MapItem } from 'src/shared/schemas/utils'

import { CashMoneyIcon } from '../icons/business-info/cash-money-icon'
import { CreditScoreIcon } from '../icons/business-info/credit-score-icon'
import { DownPaymentIcon } from '../icons/business-info/down-payment-icon'
import { EmploymentStatusIcon } from '../icons/business-info/employment-status-icon'
import { EstimatedValueIcon } from '../icons/business-info/estimated-value-icon'
import { FlagIcon } from '../icons/business-info/flag-icon'
import { IncomeIcon } from '../icons/business-info/income-icon'
import { LocationIcon } from '../icons/business-info/location-icon'
import { MailIcon } from '../icons/business-info/mail-icon'
import { MortgageBalanceIcon } from '../icons/business-info/mortgage-balance-icon'
import { NotesIcon } from '../icons/business-info/notes-icon'
import { PhoneIcon } from '../icons/business-info/phone-icon'
import { PreapprovedIcon } from '../icons/business-info/preapproved-icon'
import { PriceRangeIcon } from '../icons/business-info/price-range-icon'
import { PropertyTypeIcon } from '../icons/business-info/property-type-icon'
import { TimeframeIcon } from '../icons/business-info/timeframe-icon'
import { UserIcon } from '../icons/business-info/user-icon'
import { VeteranBadgeIcon } from '../icons/business-info/veteran-badge-icon'
import { WarningIcon } from '../icons/business-info/warning-icon'

import { businessStatusMap } from './business-status'
import { loanTypeMap } from './loan-type'
import type { LoanType } from './loan-type'

export type BusinessInfoType = // 'clientInfo' |
  'transactionInfo' | 'locationInfo' | 'financialInfo' | 'propertyDetails' | 'mortgageInfo'

export const BusinessInfoTypePerProfession: Record<Profession, BusinessInfoType[]> = {
  real_estate_agent: ['transactionInfo', 'locationInfo', 'financialInfo', 'propertyDetails'],
  mortgage_loan_officer: ['transactionInfo', 'locationInfo', 'financialInfo', 'propertyDetails', 'mortgageInfo'],
  real_estate_insurance_broker: ['transactionInfo', 'locationInfo', 'financialInfo', 'propertyDetails'],
  real_estate_attorney: ['transactionInfo', 'locationInfo', 'financialInfo', 'propertyDetails'],
  contractor: ['transactionInfo', 'locationInfo', 'financialInfo', 'propertyDetails'],
  property_inspector: ['transactionInfo', 'locationInfo', 'financialInfo', 'propertyDetails'],
  real_estate_appraiser: ['transactionInfo', 'locationInfo', 'financialInfo', 'propertyDetails'],
  title_company_rep: ['transactionInfo', 'locationInfo', 'financialInfo', 'propertyDetails'],
}

export const BusinessInfoTypeLabels = {
  // clientInfo: 'Client',
  transactionInfo: 'Deal',
  locationInfo: 'Location',
  financialInfo: 'Financials',
  propertyDetails: 'Property',
  mortgageInfo: 'Mortgage',
  // privateFields: 'Private fields',
  // customFields: 'Custom fields',
}

export const BusinessInfoTypeDescriptions = {
  // clientInfo: 'Client',
  transactionInfo: 'Information about the deal',
  locationInfo: 'Where the client is looking',
  financialInfo: "The client's financial situation",
  propertyDetails: 'The details of the property',
  mortgageInfo: "The client's mortgage situation",
  // privateFields: 'Private fields',
  // customFields: 'Custom fields',
}

export const BusinessInfoFieldsPerType: Record<BusinessInfoType, (keyof BusinessInfo)[]> = {
  // clientInfo: ['consumerFirstName', 'consumerLastName', 'consumerEmail', 'consumerPhone'],
  transactionInfo: ['clientType', 'propertyType', 'timeframe', 'desiredClosingDate', 'closedAt'],
  locationInfo: ['desiredLocations', 'exactLocations'],
  financialInfo: ['priceRange', 'exactPrice', 'budget'],
  propertyDetails: ['bedrooms', 'bathrooms', 'squareFootageEstimate', 'yearBuilt', 'lotSize'],
  mortgageInfo: ['mortgageRate', 'loanType', 'cashOutAmount', 'mortgageRateUpdatedAt', 'loanTerm', 'workingWithLender'],
  // privateFields: [],
  // customFields: [],
}

// Consumer Information
export interface BusinessInfo {
  // GRC Refinance
  loanTerm?: number | null
  mortgageRateUpdatedAt?: Date | null
  bestTimeToConnect?: Date | null
  isInterestedInRefinance?: boolean | null
  workingWithLender?: boolean | null
  cashOutAmount?: number | null
  isVeteran?: boolean | null

  // Consumer Information
  consumerId?: number | null
  consumerFirstName?: string | null
  consumerLastName?: string | null
  consumerEmail?: string | null // EmailStr assumed as string
  consumerPhone?: string | null
  consumerEmailVerified?: boolean | null
  consumerPhoneVerified?: boolean | null

  // Transaction Information
  status?: BusinessStatus | null
  clientType?: ClientType | null
  propertyType?: PropertyType | null
  residenceType?: ResidenceType | null
  closedAt?: Date | null

  // Location Information
  desiredLocations?: string[] | null
  exactLocations?: string[] | null
  propertyStreetAddress1?: string | null
  propertyStreetAddress2?: string | null
  propertyCity?: string | null
  propertyState?: string | null
  propertyZip?: string | null

  // Financial Information
  priceRange?: PriceRange | null
  exactPrice?: number | null
  budget?: Budget | null
  timeframe?: Timeframe | null

  // Property Details
  bedrooms?: number | null
  bathrooms?: number | null
  squareFootageEstimate?: number | null
  yearBuilt?: number | null
  lotSize?: number | null
  desiredClosingDate?: Date | null

  // --- Profession-Group Shared Fields (in details) ---
  // REA + LO Shared
  downPaymentEstimate?: number | null
  loanPurpose?: LoanPurpose | null
  loanType?: LoanType | null
  isPreApproved?: boolean | null

  // REA + Inspector + Contractor Shared
  knownPropertyIssues?: string | null

  // LO + Appraiser + Title Shared
  estimatedPropertyValue?: number | null
  approvedLoanAmount?: number | null
  creditScoreEstimate?: CreditScore | null
  employmentStatus?: EmploymentStatus | null

  // --- Private Workspace Fields (in details) ---
  // ReaWorkspace
  rentalType?: RentalType | null

  // MortgageLoanOfficerWorkspace
  mortgageRate?: number | null
  assetsEstimate?: number | null
  incomeEstimate?: number | null
  mortgageBalance?: number | null
  refinanceReason?: RefinanceReason | null

  // InsuranceWorkspace
  dwellingCoverage?: number | null
  liabilityCoverage?: number | null
  personalPropertyCoverage?: number | null
  deductibleAmount?: number | null
  additionalEndorsements?: number | null
  carrierName?: string | null
  coverageLevelDetails?: string | null
  pastClaims?: ClaimItem[] | null

  // AttorneyWorkspace
  attorneyTransactionType?: string | null
  contractStatus?: ContractStatus | null

  // ContractorWorkspace
  projectType?: ProjectType | null
  descriptionOfWork?: string | null
  materialPreferences?: string | null
  desiredStartDate?: Date | null
  desiredEndDate?: Date | null
  contractorBudget?: Budget | null

  // InspectorWorkspace
  additionalTestsNeeded?: string | null

  // AppraiserWorkspace
  appraisalPurpose?: string | null
  uniqueFeatures?: string | null

  // TitleWorkspace
  titleTransactionType?: string | null
  dateFlexibility?: DateFlexibility | null
  additionalServices?: string[] | null
}

export const BusinessInfoIcons: Record<
  keyof BusinessInfo,
  (props: { size: number; color: string }) => React.ReactNode
> = {
  loanTerm: ({ size, color }) => <MortgageBalanceIcon size={size} color={color} />,
  mortgageRateUpdatedAt: ({ size, color }) => <CalendarAppointmentIcon size={size} color={color} />,
  bestTimeToConnect: ({ size, color }) => <CalendarAppointmentIcon size={size} color={color} />,
  isInterestedInRefinance: ({ size, color }) => <FlagIcon size={size} color={color} />,
  workingWithLender: ({ size, color }) => <PreapprovedIcon size={size} color={color} />,
  cashOutAmount: ({ size, color }) => <CashMoneyIcon size={size} color={color} />,
  isVeteran: ({ size, color }) => <VeteranBadgeIcon size={size * 2} color={color} />,

  consumerId: ({ size, color }) => <UserIcon size={size} color={color} />,
  consumerFirstName: ({ size, color }) => <UserIcon size={size} color={color} />,
  consumerLastName: ({ size, color }) => <UserIcon size={size} color={color} />,
  consumerEmail: ({ size, color }) => <MailIcon size={size} color={color} />,
  consumerPhone: ({ size, color }) => <PhoneIcon size={size} color={color} />,
  consumerEmailVerified: ({ size, color }) => <MailIcon size={size} color={color} />,
  consumerPhoneVerified: ({ size, color }) => <PhoneIcon size={size} color={color} />,

  status: ({ size, color }) => <FlagIcon size={size} color={color} />,
  clientType: ({ size, color }) => <PropertyTypeIcon size={size} color={color} />,
  propertyType: ({ size, color }) => <PropertyTypeIcon size={size} color={color} />,
  residenceType: ({ size, color }) => <PropertyTypeIcon size={size} color={color} />,

  desiredLocations: ({ size, color }) => <LocationIcon size={size} color={color} />,
  exactLocations: ({ size, color }) => <LocationIcon size={size} color={color} />,
  propertyStreetAddress1: ({ size, color }) => <LocationIcon size={size} color={color} />,
  propertyStreetAddress2: ({ size, color }) => <LocationIcon size={size} color={color} />,
  propertyCity: ({ size, color }) => <LocationIcon size={size} color={color} />,
  propertyState: ({ size, color }) => <LocationIcon size={size} color={color} />,
  propertyZip: ({ size, color }) => <LocationIcon size={size} color={color} />,

  priceRange: ({ size, color }) => <PriceRangeIcon size={size} color={color} />,
  exactPrice: ({ size, color }) => <PriceRangeIcon size={size} color={color} />,
  budget: ({ size, color }) => <PriceRangeIcon size={size} color={color} />,
  timeframe: ({ size, color }) => <TimeframeIcon size={size} color={color} />,

  bedrooms: ({ size, color }) => <PropertyTypeIcon size={size} color={color} />,
  bathrooms: ({ size, color }) => <PropertyTypeIcon size={size} color={color} />,
  squareFootageEstimate: ({ size, color }) => <PropertyTypeIcon size={size} color={color} />,
  yearBuilt: ({ size, color }) => <PropertyTypeIcon size={size} color={color} />,
  lotSize: ({ size, color }) => <PropertyTypeIcon size={size} color={color} />,
  desiredClosingDate: ({ size, color }) => <TimeframeIcon size={size} color={color} />,
  closedAt: ({ size, color }) => <TimeframeIcon size={size} color={color} />,

  downPaymentEstimate: ({ size, color }) => <DownPaymentIcon size={size} color={color} />,
  loanPurpose: ({ size, color }) => <FlagIcon size={size} color={color} />,
  loanType: ({ size, color }) => <FlagIcon size={size} color={color} />,
  isPreApproved: ({ size, color }) => <PreapprovedIcon size={size} color={color} />,

  knownPropertyIssues: ({ size, color }) => <WarningIcon size={size} color={color} />,

  estimatedPropertyValue: ({ size, color }) => <EstimatedValueIcon size={size} color={color} />,
  approvedLoanAmount: ({ size, color }) => <IncomeIcon size={size} color={color} />,
  creditScoreEstimate: ({ size, color }) => <CreditScoreIcon size={size} color={color} />,
  employmentStatus: ({ size, color }) => <EmploymentStatusIcon size={size} color={color} />,

  rentalType: ({ size, color }) => <PropertyTypeIcon size={size} color={color} />,

  mortgageRate: ({ size, color }) => <MortgageBalanceIcon size={size} color={color} />,
  assetsEstimate: ({ size, color }) => <IncomeIcon size={size} color={color} />,
  incomeEstimate: ({ size, color }) => <IncomeIcon size={size} color={color} />,
  mortgageBalance: ({ size, color }) => <MortgageBalanceIcon size={size} color={color} />,
  refinanceReason: ({ size, color }) => <FlagIcon size={size} color={color} />,

  dwellingCoverage: ({ size, color }) => <PriceRangeIcon size={size} color={color} />,
  liabilityCoverage: ({ size, color }) => <PriceRangeIcon size={size} color={color} />,
  personalPropertyCoverage: ({ size, color }) => <PriceRangeIcon size={size} color={color} />,
  deductibleAmount: ({ size, color }) => <PriceRangeIcon size={size} color={color} />,
  additionalEndorsements: ({ size, color }) => <PriceRangeIcon size={size} color={color} />,
  carrierName: ({ size, color }) => <NotesIcon size={size} color={color} />,
  coverageLevelDetails: ({ size, color }) => <NotesIcon size={size} color={color} />,
  pastClaims: ({ size, color }) => <WarningIcon size={size} color={color} />,

  attorneyTransactionType: ({ size, color }) => <FlagIcon size={size} color={color} />,
  contractStatus: ({ size, color }) => <FlagIcon size={size} color={color} />,

  projectType: ({ size, color }) => <PropertyTypeIcon size={size} color={color} />,
  descriptionOfWork: ({ size, color }) => <NotesIcon size={size} color={color} />,
  materialPreferences: ({ size, color }) => <NotesIcon size={size} color={color} />,
  desiredStartDate: ({ size, color }) => <TimeframeIcon size={size} color={color} />,
  desiredEndDate: ({ size, color }) => <TimeframeIcon size={size} color={color} />,
  contractorBudget: ({ size, color }) => <PriceRangeIcon size={size} color={color} />,

  additionalTestsNeeded: ({ size, color }) => <WarningIcon size={size} color={color} />,

  appraisalPurpose: ({ size, color }) => <FlagIcon size={size} color={color} />,
  uniqueFeatures: ({ size, color }) => <NotesIcon size={size} color={color} />,

  titleTransactionType: ({ size, color }) => <FlagIcon size={size} color={color} />,
  dateFlexibility: ({ size, color }) => <TimeframeIcon size={size} color={color} />,
  additionalServices: ({ size, color }) => <NotesIcon size={size} color={color} />,
}

export const BusinessInfoLabels: Record<keyof BusinessInfo, string> = {
  consumerId: 'Consumer ID',
  consumerFirstName: 'First name',
  consumerLastName: 'Last name',
  consumerEmail: 'Email',
  consumerPhone: 'Phone',
  consumerEmailVerified: 'Email verified',
  consumerPhoneVerified: 'Phone verified',
  status: 'Status',
  clientType: 'Type',
  propertyType: 'Property type',
  residenceType: 'Residence type',
  desiredLocations: 'General',
  exactLocations: 'Specific property',
  propertyStreetAddress1: 'Street address 1',
  propertyStreetAddress2: 'Street address 2',
  propertyCity: 'City',
  propertyState: 'State',
  propertyZip: 'Zip',
  priceRange: 'Price range',
  exactPrice: 'Exact price',
  budget: 'Budget (lease)',
  timeframe: 'Timeframe',
  bedrooms: 'Bedroom(s)',
  bathrooms: 'Bathroom(s)',
  squareFootageEstimate: 'Square footage',
  yearBuilt: 'Year built',
  lotSize: 'Lot size',
  desiredClosingDate: 'Closing date',
  closedAt: 'Closed date',
  downPaymentEstimate: 'Down payment estimate',
  loanPurpose: 'Loan purpose',
  loanType: 'Loan type',
  isPreApproved: 'Is pre-approved',
  knownPropertyIssues: 'Known property issues',
  estimatedPropertyValue: 'Estimated property value',
  approvedLoanAmount: 'Approved loan amount',
  creditScoreEstimate: 'Credit score estimate',
  employmentStatus: 'Employment status',
  rentalType: 'Rental type',
  mortgageRate: 'Mortgage rate',
  assetsEstimate: 'Assets estimate',
  incomeEstimate: 'Income estimate',
  mortgageBalance: 'Mortgage balance',
  refinanceReason: 'Refinance reason',
  dwellingCoverage: 'Dwelling coverage',
  liabilityCoverage: 'Liability coverage',
  personalPropertyCoverage: 'Personal property coverage',
  deductibleAmount: 'Deductible amount',
  additionalEndorsements: 'Additional endorsements',
  carrierName: 'Carrier name',
  coverageLevelDetails: 'Coverage level details',
  pastClaims: 'Past claims',
  attorneyTransactionType: 'Attorney transaction type',
  contractStatus: 'Contract status',
  projectType: 'Project type',
  descriptionOfWork: 'Description of work',
  materialPreferences: 'Material preferences',
  desiredStartDate: 'Desired start date',
  desiredEndDate: 'Desired end date',
  contractorBudget: 'Contractor budget',
  additionalTestsNeeded: 'Additional tests needed',
  appraisalPurpose: 'Appraisal purpose',
  uniqueFeatures: 'Unique features',
  titleTransactionType: 'Title transaction type',
  dateFlexibility: 'Date flexibility',
  additionalServices: 'Additional services',
  bestTimeToConnect: 'Best time to connect',
  mortgageRateUpdatedAt: 'Mortgage rate updated at',
  isInterestedInRefinance: 'Interested in refinancing',
  workingWithLender: 'Working with another lender',
  cashOutAmount: 'Cash-out amount',
  isVeteran: 'Veteran',
  loanTerm: 'Loan term',
}

export const BusinessInfoAliases: Record<keyof BusinessInfo, string> = {
  consumerId: 'consumer_id',
  consumerFirstName: 'consumer_first_name',
  consumerLastName: 'consumer_last_name',
  consumerEmail: 'consumer_email',
  consumerPhone: 'consumer_phone',
  consumerEmailVerified: 'consumer_email_verified',
  consumerPhoneVerified: 'consumer_phone_verified',
  status: 'status',
  clientType: 'client_type',
  propertyType: 'property_type',
  residenceType: 'residence_type',
  desiredLocations: 'desired_locations',
  exactLocations: 'exact_locations',
  propertyStreetAddress1: 'property_street_address_1',
  propertyStreetAddress2: 'property_street_address_2',
  propertyCity: 'property_city',
  propertyState: 'property_state',
  propertyZip: 'property_zip',
  priceRange: 'price_range',
  exactPrice: 'exact_price',
  budget: 'budget',
  timeframe: 'timeframe',
  bedrooms: 'bedrooms',
  bathrooms: 'bathrooms',
  squareFootageEstimate: 'square_footage_estimate',
  yearBuilt: 'year_built',
  lotSize: 'lot_size',
  desiredClosingDate: 'desired_closing_date',
  closedAt: 'closed_at',
  downPaymentEstimate: 'down_payment_estimate',
  loanPurpose: 'loan_purpose',
  loanType: 'loan_type',
  isPreApproved: 'is_pre_approved',
  knownPropertyIssues: 'known_property_issues',
  estimatedPropertyValue: 'estimated_property_value',
  approvedLoanAmount: 'approved_loan_amount',
  creditScoreEstimate: 'credit_score_estimate',
  employmentStatus: 'employment_status',
  rentalType: 'rental_type',
  mortgageRate: 'mortgage_rate',
  assetsEstimate: 'assets_estimate',
  incomeEstimate: 'income_estimate',
  mortgageBalance: 'mortgage_balance',
  refinanceReason: 'refinance_reason',
  dwellingCoverage: 'dwelling_coverage',
  liabilityCoverage: 'liability_coverage',
  personalPropertyCoverage: 'personal_property_coverage',
  deductibleAmount: 'deductible_amount',
  additionalEndorsements: 'additional_endorsements',
  carrierName: 'carrier_name',
  coverageLevelDetails: 'coverage_level_details',
  pastClaims: 'past_claims',
  attorneyTransactionType: 'attorney_transaction_type',
  contractStatus: 'contract_status',
  projectType: 'project_type',
  descriptionOfWork: 'description_of_work',
  materialPreferences: 'material_preferences',
  desiredStartDate: 'desired_start_date',
  desiredEndDate: 'desired_end_date',
  contractorBudget: 'contractor_budget',
  additionalTestsNeeded: 'additional_tests_needed',
  appraisalPurpose: 'appraisal_purpose',
  uniqueFeatures: 'unique_features',
  titleTransactionType: 'title_transaction_type',
  dateFlexibility: 'date_flexibility',
  additionalServices: 'additional_services',
  bestTimeToConnect: 'best_time_to_connect',
  mortgageRateUpdatedAt: 'mortgage_rate_updated_at',
  isInterestedInRefinance: 'is_interested_in_refinance',
  workingWithLender: 'working_with_lender',
  cashOutAmount: 'cash_out_amount',
  isVeteran: 'is_veteran',
  loanTerm: 'loan_term',
}

export enum FieldTypeEnum {
  TEXT = 'text',
  NUMBER = 'number',
  DATE = 'date',
  BOOLEAN = 'boolean',
  SELECT = 'select',
  PHONE = 'phone',
  LOCATIONS = 'locations',
  RANGE = 'range',
  PRICE_RANGE = 'price_range',
  CURRENCY = 'currency',
  EXACT_LOCATIONS = 'exact_locations',
  RATE = 'rate',
}

export const BusinessInfoFieldsEditType: Record<keyof BusinessInfo, FieldTypeEnum> = {
  consumerFirstName: FieldTypeEnum.TEXT,
  consumerLastName: FieldTypeEnum.TEXT,
  consumerEmail: FieldTypeEnum.TEXT,
  consumerPhone: FieldTypeEnum.PHONE,
  status: FieldTypeEnum.SELECT,
  clientType: FieldTypeEnum.SELECT,
  propertyType: FieldTypeEnum.SELECT,
  residenceType: FieldTypeEnum.SELECT,
  desiredLocations: FieldTypeEnum.LOCATIONS,
  exactLocations: FieldTypeEnum.EXACT_LOCATIONS,
  propertyStreetAddress1: FieldTypeEnum.TEXT,
  propertyStreetAddress2: FieldTypeEnum.TEXT,
  propertyCity: FieldTypeEnum.TEXT,
  propertyState: FieldTypeEnum.TEXT,
  propertyZip: FieldTypeEnum.TEXT,
  priceRange: FieldTypeEnum.PRICE_RANGE,
  exactPrice: FieldTypeEnum.CURRENCY,
  budget: FieldTypeEnum.PRICE_RANGE,
  timeframe: FieldTypeEnum.SELECT,
  bedrooms: FieldTypeEnum.NUMBER,
  bathrooms: FieldTypeEnum.NUMBER,
  squareFootageEstimate: FieldTypeEnum.NUMBER,
  yearBuilt: FieldTypeEnum.NUMBER,
  lotSize: FieldTypeEnum.NUMBER,
  desiredClosingDate: FieldTypeEnum.DATE,
  closedAt: FieldTypeEnum.DATE,
  downPaymentEstimate: FieldTypeEnum.CURRENCY,
  loanPurpose: FieldTypeEnum.SELECT,
  loanType: FieldTypeEnum.SELECT,
  isPreApproved: FieldTypeEnum.BOOLEAN,
  knownPropertyIssues: FieldTypeEnum.TEXT,
  estimatedPropertyValue: FieldTypeEnum.CURRENCY,
  approvedLoanAmount: FieldTypeEnum.CURRENCY,
  creditScoreEstimate: FieldTypeEnum.SELECT,
  employmentStatus: FieldTypeEnum.SELECT,
  rentalType: FieldTypeEnum.SELECT,
  mortgageRate: FieldTypeEnum.RATE,
  assetsEstimate: FieldTypeEnum.CURRENCY,
  incomeEstimate: FieldTypeEnum.CURRENCY,
  mortgageBalance: FieldTypeEnum.CURRENCY,
  refinanceReason: FieldTypeEnum.SELECT,
  dwellingCoverage: FieldTypeEnum.CURRENCY,
  liabilityCoverage: FieldTypeEnum.CURRENCY,
  personalPropertyCoverage: FieldTypeEnum.CURRENCY,
  deductibleAmount: FieldTypeEnum.CURRENCY,
  additionalEndorsements: FieldTypeEnum.CURRENCY,
  carrierName: FieldTypeEnum.TEXT,
  coverageLevelDetails: FieldTypeEnum.TEXT,
  pastClaims: FieldTypeEnum.SELECT,
  attorneyTransactionType: FieldTypeEnum.TEXT,
  contractStatus: FieldTypeEnum.SELECT,
  projectType: FieldTypeEnum.SELECT,
  descriptionOfWork: FieldTypeEnum.TEXT,
  materialPreferences: FieldTypeEnum.TEXT,
  desiredStartDate: FieldTypeEnum.DATE,
  desiredEndDate: FieldTypeEnum.DATE,
  contractorBudget: FieldTypeEnum.PRICE_RANGE,
  additionalTestsNeeded: FieldTypeEnum.TEXT,
  appraisalPurpose: FieldTypeEnum.TEXT,
  uniqueFeatures: FieldTypeEnum.TEXT,
  titleTransactionType: FieldTypeEnum.TEXT,
  dateFlexibility: FieldTypeEnum.SELECT,
  additionalServices: FieldTypeEnum.SELECT,
  consumerId: FieldTypeEnum.NUMBER,
  consumerEmailVerified: FieldTypeEnum.BOOLEAN,
  consumerPhoneVerified: FieldTypeEnum.BOOLEAN,
  bestTimeToConnect: FieldTypeEnum.DATE,
  mortgageRateUpdatedAt: FieldTypeEnum.DATE,
  isInterestedInRefinance: FieldTypeEnum.BOOLEAN,
  workingWithLender: FieldTypeEnum.BOOLEAN,
  cashOutAmount: FieldTypeEnum.CURRENCY,
  isVeteran: FieldTypeEnum.BOOLEAN,
  loanTerm: FieldTypeEnum.NUMBER,
}

export enum DisplayTypeEnum {
  TEXT = 'text',
  RANGE = 'range',
  PRICE_RANGE = 'price_range',
  DATE = 'date',
  BOOLEAN = 'boolean',
  LIST = 'list',
  CURRENCY = 'currency',
  ENUM = 'enum',
  NUMBER = 'number',
  PHONE = 'phone',
  DESIRED_LOCATIONS = 'desired_locations',
  EXACT_LOCATIONS = 'exact_locations',
  RATE = 'rate',
}

type BusinessEnumKeys =
  | 'status'
  | 'clientType'
  | 'propertyType'
  | 'timeframe'
  | 'creditScoreEstimate'
  | 'employmentStatus'
  | 'rentalType'
  | 'refinanceReason'
  | 'contractStatus'
  | 'projectType'
  | 'dateFlexibility'
  | 'titleTransactionType'
  | 'loanPurpose'
  | 'loanType'
  | 'residenceType'

export const BusinessEnumMap: Record<BusinessEnumKeys, Record<string, MapItem>> = {
  status: businessStatusMap,
  clientType: clientTypeMap,
  propertyType: propertyTypeMap,
  residenceType: residenceTypeMap,
  timeframe: timeframeMap,
  creditScoreEstimate: creditScoreMap,
  employmentStatus: employmentStatusMap,
  rentalType: rentalTypeMap,
  refinanceReason: refinanceReasonMap,
  contractStatus: contractStatusMap,
  projectType: projectTypeMap,
  dateFlexibility: dateFlexibilityMap,
  titleTransactionType: titleTransactionTypeMap,
  loanPurpose: loanPurposeMap,
  loanType: loanTypeMap,
}

export const BusinessInfoFieldsDisplayType: Record<keyof BusinessInfo, DisplayTypeEnum> = {
  consumerId: DisplayTypeEnum.TEXT,
  consumerFirstName: DisplayTypeEnum.TEXT,
  consumerLastName: DisplayTypeEnum.TEXT,
  consumerEmail: DisplayTypeEnum.TEXT,
  consumerPhone: DisplayTypeEnum.PHONE,
  consumerEmailVerified: DisplayTypeEnum.BOOLEAN,
  consumerPhoneVerified: DisplayTypeEnum.BOOLEAN,
  status: DisplayTypeEnum.ENUM,
  clientType: DisplayTypeEnum.ENUM,
  propertyType: DisplayTypeEnum.ENUM,
  residenceType: DisplayTypeEnum.ENUM,
  desiredLocations: DisplayTypeEnum.DESIRED_LOCATIONS,
  exactLocations: DisplayTypeEnum.EXACT_LOCATIONS,
  propertyStreetAddress1: DisplayTypeEnum.TEXT,
  propertyStreetAddress2: DisplayTypeEnum.TEXT,
  propertyCity: DisplayTypeEnum.TEXT,
  propertyState: DisplayTypeEnum.TEXT,
  propertyZip: DisplayTypeEnum.TEXT,
  priceRange: DisplayTypeEnum.PRICE_RANGE,
  exactPrice: DisplayTypeEnum.CURRENCY,
  budget: DisplayTypeEnum.PRICE_RANGE,
  timeframe: DisplayTypeEnum.ENUM,
  bedrooms: DisplayTypeEnum.NUMBER,
  bathrooms: DisplayTypeEnum.NUMBER,
  squareFootageEstimate: DisplayTypeEnum.NUMBER,
  yearBuilt: DisplayTypeEnum.NUMBER,
  lotSize: DisplayTypeEnum.NUMBER,
  desiredClosingDate: DisplayTypeEnum.DATE,
  closedAt: DisplayTypeEnum.DATE,
  downPaymentEstimate: DisplayTypeEnum.CURRENCY,
  loanPurpose: DisplayTypeEnum.ENUM,
  loanType: DisplayTypeEnum.ENUM,
  isPreApproved: DisplayTypeEnum.BOOLEAN,
  knownPropertyIssues: DisplayTypeEnum.TEXT,
  estimatedPropertyValue: DisplayTypeEnum.CURRENCY,
  approvedLoanAmount: DisplayTypeEnum.CURRENCY,
  creditScoreEstimate: DisplayTypeEnum.ENUM,
  employmentStatus: DisplayTypeEnum.ENUM,
  rentalType: DisplayTypeEnum.ENUM,
  mortgageRate: DisplayTypeEnum.RATE,
  assetsEstimate: DisplayTypeEnum.CURRENCY,
  incomeEstimate: DisplayTypeEnum.CURRENCY,
  mortgageBalance: DisplayTypeEnum.CURRENCY,
  refinanceReason: DisplayTypeEnum.ENUM,
  dwellingCoverage: DisplayTypeEnum.CURRENCY,
  liabilityCoverage: DisplayTypeEnum.CURRENCY,
  personalPropertyCoverage: DisplayTypeEnum.CURRENCY,
  deductibleAmount: DisplayTypeEnum.CURRENCY,
  additionalEndorsements: DisplayTypeEnum.CURRENCY,
  carrierName: DisplayTypeEnum.TEXT,
  coverageLevelDetails: DisplayTypeEnum.TEXT,
  pastClaims: DisplayTypeEnum.LIST,
  attorneyTransactionType: DisplayTypeEnum.TEXT,
  contractStatus: DisplayTypeEnum.ENUM,
  projectType: DisplayTypeEnum.ENUM,
  descriptionOfWork: DisplayTypeEnum.TEXT,
  materialPreferences: DisplayTypeEnum.TEXT,
  desiredStartDate: DisplayTypeEnum.DATE,
  desiredEndDate: DisplayTypeEnum.DATE,
  contractorBudget: DisplayTypeEnum.RANGE,
  additionalTestsNeeded: DisplayTypeEnum.TEXT,
  appraisalPurpose: DisplayTypeEnum.TEXT,
  uniqueFeatures: DisplayTypeEnum.TEXT,
  titleTransactionType: DisplayTypeEnum.TEXT,
  dateFlexibility: DisplayTypeEnum.ENUM,
  additionalServices: DisplayTypeEnum.LIST,
  bestTimeToConnect: DisplayTypeEnum.DATE,
  mortgageRateUpdatedAt: DisplayTypeEnum.DATE,
  isInterestedInRefinance: DisplayTypeEnum.BOOLEAN,
  workingWithLender: DisplayTypeEnum.BOOLEAN,
  cashOutAmount: DisplayTypeEnum.CURRENCY,
  isVeteran: DisplayTypeEnum.BOOLEAN,
  loanTerm: DisplayTypeEnum.NUMBER,
}
